/**
 * The tags Flashroom sets on a person, per webinar, on its own. Everything an
 * automation can start from, stop on, or ask about is a tag: these, or the
 * host's own. See notes/automations-on-tags.md.
 */
import type { TriggerJson } from "./types";

/** Watched percent at or above which a person has finished the talk: the `completed` tag. */
export const COMPLETE_PCT = 95;

export interface SystemTag {
  name: string;
  /** When Flashroom adds it, in the host's words. */
  when: string;
  /** Can Flashroom take it away again (so "loses the tag" means something)? */
  removable?: boolean;
  /** True for a tag that is true of one session and says nothing about any other. */
  perSession: boolean;
}

export const SYSTEM_TAGS: readonly SystemTag[] = [
  { name: "registered", when: "the moment they register", perSession: false },
  { name: "watched", when: "first time the talk plays", perSession: false },
  { name: "completed", when: "played to the end", perSession: false },
  { name: "stopped-watching", when: "no play for a while, before the end", removable: true, perSession: false },
  { name: "reached-offer", when: "the offer's minute on the talk", perSession: false },
  { name: "clicked-offer", when: "pressed the offer button", perSession: false },
  { name: "watched-offer-video", when: "pressed play on the offer's video", perSession: false },
  { name: "finished-offer-video", when: "played the offer's video to the end", perSession: false },
  { name: "replied", when: "answered an email", perSession: false },
  { name: "unsubscribed", when: "asked to be left alone", perSession: false },
  { name: "came-live", when: "joined that session", perSession: true },
  { name: "stayed-to-the-end", when: "still there when it ended", perSession: true },
  { name: "left-early", when: "went before the end", removable: true, perSession: true },
  { name: "asked-a-question", when: "asked something in the room", perSession: true },
  { name: "watched-again", when: "came back to something they had finished", perSession: false },
  { name: "finished-again", when: "finished it a second time", perSession: false },
];

export const SYSTEM_TAG_NAMES: readonly string[] = SYSTEM_TAGS.map((t) => t.name);

export function isSystemTag(name: string): boolean {
  return SYSTEM_TAG_NAMES.includes(normalizeTag(name));
}

/**
 * Is this tag true of one session only? The tag picker groups by it and the
 * trigger card asks before it offers the How often choice. A host's own tag is
 * about the person, so it is never per session.
 */
export function isPerSessionTag(name: string): boolean {
  const tag = normalizeTag(name);
  return SYSTEM_TAGS.some((t) => t.name === tag && t.perSession);
}

/**
 * The slot a tag hangs in: person + webinar + session. A thing that does not
 * repeat is its own single session, which is what migration 0060 made true for
 * every live, so no code has to ask whether a webinar is a series.
 */
export function sessionSlot(webinarId: string, sessionId?: string | null): string {
  return sessionId || webinarId;
}

/** Tags are lowercase, hyphenated words: "Booked Call" becomes "booked-call". */
export function normalizeTag(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .slice(0, 48)
    .replace(/^-|-$/g, "");
}

/** How long with no play counts as stopped, when the workspace has not said. */
export const DEFAULT_IDLE_MINUTES = 10;

/**
 * The trigger types that came before tags, read as the tag they meant. The
 * ones with no tag of their own (a window, a band, a session clock) load as
 * `registered` and the caller switches the automation off, so the host
 * rebuilds it from Wait and Branch with a number of their own.
 */
const LEGACY_TAG: Record<string, string> = {
  registers: "registered",
  attends: "watched",
  watches_replay: "watched",
  completes: "completed",
  finishes_replay: "completed",
  reaches_offer: "reached-offer",
  clicks_offer: "clicked-offer",
  signs_up: "customer",
  unsubscribes: "unsubscribed",
};

interface ReadTrigger {
  trigger: TriggerJson;
  /** True when the old trigger had no tag equivalent and the meaning was lost. */
  lossy: boolean;
}

/** Read any stored trigger, old or new, as a tag trigger. */
export function legacyTrigger(raw: unknown): ReadTrigger {
  const t = (raw && typeof raw === "object" ? raw : {}) as {
    type?: unknown;
    value?: unknown;
    keep_going?: unknown;
    how_often?: unknown;
  };
  const type = typeof t.type === "string" ? t.type : "";
  if (type === "tag_added" || type === "tag_removed") {
    const value = normalizeTag(typeof t.value === "string" ? t.value : "");
    const keep = type === "tag_added" && t.keep_going === true;
    const often = t.how_often === "each" ? ({ how_often: "each" } as const) : {};
    return { trigger: { type, value: value || "registered", ...(keep ? { keep_going: true } : {}), ...often }, lossy: !value };
  }
  const mapped = LEGACY_TAG[type];
  if (mapped) return { trigger: { type: "tag_added", value: mapped }, lossy: false };
  return { trigger: { type: "tag_added", value: "registered" }, lossy: true };
}

/**
 * Can this tag come off a person again? Most of Flashroom's own tags are
 * facts that stay (registered, watched, completed). A host's own tag can
 * always be taken away.
 */
export function tagCanComeOff(name: string): boolean {
  const tag = normalizeTag(name);
  const sys = SYSTEM_TAGS.find((t) => t.name === tag);
  return sys ? sys.removable === true : true;
}

/**
 * The tag whose loss ends a run of this automation on its own, or null when
 * nothing does: the automation starts on losing a tag, the host switched it to
 * keep going, or the tag is one that never comes off.
 */
export function endsOnLostTag(trigger: TriggerJson | null | undefined): string | null {
  if (!trigger || trigger.type !== "tag_added" || trigger.keep_going === true) return null;
  const tag = normalizeTag(trigger.value ?? "");
  return tag && tagCanComeOff(tag) ? tag : null;
}

/**
 * Does this automation start again each session, or once ever? Absent means
 * once, which is how every automation written before sessions behaves.
 */
export function startsEachSession(trigger: TriggerJson | null | undefined): boolean {
  return trigger?.how_often === "each";
}

/** "When someone gets the tag registered", the one sentence a trigger is. */
export function triggerSentence(trigger: TriggerJson): string {
  const verb = trigger.type === "tag_removed" ? "loses the tag" : "gets the tag";
  return `When someone ${verb} ${trigger.value}`;
}
